<template>
	<app-button @click="open = true">Add Product Type</app-button>

	<slideover title="Add Product Type" :open="open" @close="close">
		<div v-if="apiError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
			<div class="flex">
				<div class="flex-shrink-0">
					<svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
						<path
							fill-rule="evenodd"
							d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
							clip-rule="evenodd" />
					</svg>
				</div>
				<div class="ml-3">
					<h3 class="text-sm font-medium text-red-800">Error</h3>
					<div class="mt-2 text-sm text-red-700">
						<p>{{ apiError }}</p>
					</div>
				</div>
			</div>
		</div>

		<app-input
			label="Name"
			:model-value="name"
			:disabled="submitting"
			:error="nameError"
			@update:model-value="name = $event; clearApiError()"
			@blur="nameBlur">
		</app-input>

		<app-input
			label="Summary"
			type="textarea"
			:model-value="summary"
			:disabled="submitting"
			:error="summaryError"
			@update:model-value="summary = $event; clearApiError()"
			@blur="summaryBlur">
		</app-input>

		<app-input
			label="Description"
			type="textarea"
			:model-value="description"
			:disabled="submitting"
			:error="descriptionError"
			@update:model-value="description = $event; clearApiError()"
			@blur="descriptionBlur">
		</app-input>

		<app-input
			label="Features & Benefits"
			type="textarea"
			:model-value="featuresBenefits"
			:disabled="submitting"
			:error="featuresBenefitsError"
			@update:model-value="featuresBenefits = $event; clearApiError()"
			@blur="featuresBenefitsBlur">
		</app-input>

		<template #action>
			<div class="flex justify-end w-full">
				<app-button @click="submit">Save</app-button>
			</div>
		</template>
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, watch } from 'vue'

	import { useForm, useField } from 'vee-validate'
	import * as Yup from 'yup'
	import ProductTypeService from '@/services/product-type'
	import useToaster from '@/stores/toaster'

	const submitting = ref<boolean>(false)
	const apiError = ref<string>('')

	const props = defineProps<{
		category: string
	}>()

	const toaster = useToaster()
	const productTypeService = new ProductTypeService()

	const open = ref(false)

	const emit = defineEmits(['completed'])

	const { resetForm, handleSubmit, setFieldValue, setFieldError } = useForm<{
		name: string
		summary: string
		description: string
		features_benefits: string
		category_id: string
	}>({
		initialValues: {
			name: '',
			summary: '',
			description: '',
			features_benefits: '',
			category_id: props.category ?? ''
		},
		validationSchema: Yup.object({
			name: Yup.string()
				.min(5, 'Name must be at least 5 characters')
				.max(255, 'Name must be at most 255 characters')
				.required('Name is required'),
			summary: Yup.string()
				.nullable()
				.min(5, 'Summary must be at least 5 characters')
				.max(255, 'Summary must be at most 255 characters'),
			description: Yup.string()
				.nullable()
				.min(5, 'Description must be at least 5 characters')
				.max(255, 'Description must be at most 255 characters'),
			features_benefits: Yup.string()
				.nullable()
				.min(5, 'Features & Benefits must be at least 5 characters')
				.max(255, 'Features & Benefits must be at most 255 characters')
		}),
		validateOnMount: false,
		validateOnBlur: true
	})

	const { value: name, errorMessage: nameError, handleBlur: nameBlur } = useField('name')
	const {
		value: summary,
		errorMessage: summaryError,
		handleBlur: summaryBlur
	} = useField('summary')
	const {
		value: description,
		errorMessage: descriptionError,
		handleBlur: descriptionBlur
	} = useField('description')
	const {
		value: featuresBenefits,
		errorMessage: featuresBenefitsError,
		handleBlur: featuresBenefitsBlur
	} = useField('features_benefits')

	watch(
		() => props.category,
		(newCategory) => {
			if (newCategory) {
				setFieldValue('category_id', newCategory)
			}
		}
	)

	const submit = handleSubmit(
		(values) => {
			submitting.value = true

			productTypeService
				.create(values)
				.then((result) => {
					toaster.success({ text: values.name + ' created' })
					close()
					emit('completed', result.data)
				})
				.catch((error) => {
					console.error('Error creating product type:', error)

					// Handle API validation errors
					if (error.response?.data?.errors) {
						const apiErrors = error.response.data.errors

						// Set field errors from API response
						if (apiErrors.name) {
							setFieldError('name', apiErrors.name[0])
							apiError.value = apiErrors.name[0]
						}

						toaster.error({ text: 'Validation failed. Please check the form.' })
					} else if (error.response?.data?.name) {
						// Handle case where name error is at the root level
						const nameError = Array.isArray(error.response.data.name)
							? error.response.data.name[0]
							: error.response.data.name
						setFieldError('name', nameError)
						apiError.value = nameError
						toaster.error({ text: 'Validation failed. Please check the form.' })
					} else {
						apiError.value = 'Failed to create product type. Please try again.'
						toaster.error({ text: 'Failed to create product type' })
					}
				})
				.finally(() => {
					submitting.value = false
				})
		},
		(errors) => {
			console.error(errors)
		}
	)

	function close() {
		open.value = false
		resetForm()
		apiError.value = ''
	}

	function clearApiError() {
		if (apiError.value) {
			apiError.value = ''
		}
	}
</script>
